export interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  description?: string;
  features?: string[];
  improvements?: string[];
  fixes?: string[];
}

export const changelog: ChangelogEntry[] = [
  {
    version: "0.34.0",
    date: "October 5, 2026",
    title: "Manage Your Organization with API Keys",
    description:
      "Your integrations can now help run your organization, not just collect feedback. API keys and connected apps can work with organization settings, while new safeguards keep sensitive details and powerful actions in the right hands. Shared forms also look right for every brand.",
    features: [
      "Use API keys and connected apps to manage your organization — integrations can now read and update organization details without someone signing in",
      "Shared forms now take on the colors and logo of the brand they're viewed through, so every brand's customers see a familiar look",
    ],
    improvements: [
      "Editing a shared form from another brand now creates that brand's own copy, so your changes never affect the original that other brands rely on",
      "Only organization owners can create API keys with full admin access, and the admin option is clearly marked owner-only for everyone else",
      "Removing a verified domain is now its own permission, so you can decide exactly who on your team is allowed to do it",
      "Limited-access API keys can no longer see sensitive details like webhook secrets or teammates' contact information",
      "Actions that should always come from a real person — like managing members, billing, and deleting the organization — now require someone to be signed in",
    ],
    fixes: [
      "Your own copy of a shared form no longer shows response stats carried over from the original",
      "Partners using the API to list a brand's forms now get that brand's colors for shared forms",
    ],
  },
  {
    version: "0.33.0",
    date: "July 14, 2026",
    title: "Share Forms Across Brands",
    description:
      "One feedback form can now serve your whole organization. Share a form with every brand in one click, or clone an existing form as the starting point for your next one — plus cleaner notification emails and a smoother passkey sign-in.",
    features: [
      "Share a feedback form across all brands in your organization — turn on sharing and every brand can use the same form, no more maintaining duplicate copies",
      "Clone any feedback form — duplicate a form with all its questions and settings as a starting point, instead of building from scratch",
    ],
    improvements: [
      "Notification emails about new responses are now brand-neutral, and a new organization setting lets you choose whether they include a link back to the dashboard",
      "Signing in with a passkey no longer asks for your authenticator code afterwards — your passkey already proved it's you",
      "Forms shared across brands now show up correctly for partners using the API to list a brand's forms",
    ],
    fixes: [
      "Notification settings now tell you when they fail to load or save, instead of silently discarding your changes",
    ],
  },
  {
    version: "0.32.0",
    date: "July 8, 2026",
    title: "Search Feedback by Meaning",
    description:
      "Search now understands what you mean, not just the words you type. Ask for \u201cusers frustrated with checkout\u201d and find every related comment \u2014 even ones that never use those words.",
    features: [
      "Semantic search \u2014 searches now match by meaning using AI, so \u201cpaying was hard\u201d shows up when you search for \u201ccheckout problems\u201d. Results are ranked by relevance, with classic text matching as a safety net",
      "New feedback is indexed for semantic search automatically the moment it arrives \u2014 from widgets, the API, Zapier, or AI agents",
    ],
    improvements: [
      "A new Developers page shows everything you can build with the feedback API \u2014 quickstart, endpoints, webhooks, and MCP setup at pollenate.dev/developers",
      "A new AI page tours Pollenate's AI features in one place \u2014 the AI form builder, AI insights, semantic search, and agent integrations at pollenate.dev/ai",
    ],
    fixes: [
      "Visiting a link that doesn't exist now shows a friendly page-not-found screen instead of a blank page",
    ],
  },
  {
    version: "0.31.0",
    date: "June 25, 2026",
    title: "Connect Apps with OAuth",
    description:
      "Let trusted apps work with your Pollenate account securely — no more copying and pasting API keys. Approve exactly what each app can do, see everything that's connected, and disconnect any of them in one click.",
    features: [
      "Connect apps over OAuth — authorize a third-party app (like BlueHive HUM) to access your account through a secure approval screen, instead of sharing a long-lived API key",
      "You decide what each app can do — before you approve, the consent screen shows exactly what's being requested (read your forms, submit responses) and which organization it applies to",
      "New Connected Apps section in Settings — see every app you've authorized, when it was last used, and disconnect any of them in one click",
      "Connected apps can submit responses on a respondent's behalf — ideal for partners running your feedback forms as AI phone or SMS surveys, with every response tagged by where it came from",
    ],
    improvements: [
      "API documentation now covers the OAuth connection flow and the new fields that record where an API response came from",
    ],
  },
  {
    version: "0.30.1",
    date: "June 23, 2026",
    title: "Reliable Submissions for Long Surveys",
    description:
      "A fix so your longest feedback pages submit smoothly, no matter how many questions they have.",
    fixes: [
      "Feedback pages with 100 or more questions now submit reliably — previously a very long survey could fail with an error on the final step",
    ],
  },
  {
    version: "0.30.0",
    date: "June 22, 2026",
    title: "New Guides & Resources",
    description:
      "We've added a library of practical guides and clearer product pages to help you get more out of your feedback — and help new teammates and customers find their way around.",
    features: [
      "New guides covering the essentials — how to add a feedback widget to any site, how to measure NPS, when to use NPS vs CSAT vs CES, collecting feedback in React and Next.js, and HIPAA-compliant feedback collection",
      "A dedicated pricing page so it's easier to see what each plan includes at a glance",
      "Side-by-side comparisons with popular feedback and survey tools, to help you and your stakeholders see how Pollenate stacks up",
    ],
    improvements: [
      "Richer link previews when you share Pollenate pages on social media or in chat — each page now gets its own preview image",
    ],
  },
  {
    version: "0.29.1",
    date: "June 19, 2026",
    title: "Live Results Polish",
    description:
      "A couple of fixes for the new live polling results, so shared result screens stay accurate and update smoothly during a busy session.",
    fixes: [
      'Live results now recover correctly when a presenter reopens submissions — viewers on the lightweight fallback view no longer get stuck showing “Submissions closed”',
    ],
    improvements: [
      "Live results update more efficiently during high-traffic sessions, trimming unnecessary database work on every refresh",
    ],
  },
  {
    version: "0.29.0",
    date: "June 19, 2026",
    title: "Live Polling Results",
    description:
      "Turn any feedback page into a live audience experience — results update on screen in real time as people respond, perfect for presentations, workshops, and events.",
    features: [
      "Live results for feedback pages — switch it on per page and respondents watch anonymized, aggregated results update the instant they submit",
      "Full-screen public results page you can project on a big screen, so a whole room can watch the responses roll in together",
      "Presenter console with a built-in countdown timer — start a timer to focus the room, and submissions automatically close the moment it runs out",
      "Privacy first: live results only ever show anonymized totals — names, emails, and other personal details are never revealed on a shared screen",
      "Per-question control over what's shown live — reveal the full chart, show just a response count, or keep a question hidden from the live view",
    ],
  },
  {
    version: "0.28.0",
    date: "June 16, 2026",
    title: "A Narrower “List” Scope for API Keys",
    description:
      "Give your integrations exactly the access they need — a new API key scope can list your feedback pages without unlocking the responses inside them.",
    features: [
      'New “list” scope when creating API keys — lets a key see which feedback pages exist without granting access to individual responses, ideal for integrations that only need to discover your pages',
      'The feedback pages listing endpoint now accepts keys carrying either the new “list” scope or the broader “read” scope, so your existing keys keep working unchanged',
    ],
  },
  {
    version: "0.27.0",
    date: "June 12, 2026",
    title: "Collect Files & Voice Recordings",
    description:
      "Two new question types let respondents share more than words — attach files or record a voice message right inside your feedback pages.",
    features: [
      "New File Upload question — let respondents attach screenshots, documents, and other files to their feedback; choose what's allowed (any file, images, documents, audio, or video), how many files (up to 5), and the size limit per file",
      "New Voice Recording question — respondents record an audio message directly in the browser, with a recording length limit you control, so you can capture tone and detail that text can't",
      "Uploaded files and recordings are saved securely and shown alongside each response",
      "Both types are available to the AI form generator and the public API",
    ],
    improvements: [
      'Documentation, OpenAPI spec, and marketing pages updated to cover the two new types (now 18 question types)',
    ],
  },
  {
    version: "0.26.1",
    date: "June 10, 2026",
    title: "A Crisper Desktop Icon",
    description:
      "The Pollenate icon in your menu bar now follows your system theme, so it stays sharp and easy to spot in both light and dark mode.",
    improvements: [
      "Desktop menu-bar icon now adapts to light and dark mode — a dark hive on light menu bars and a light hive on dark ones, switching the instant you change your system appearance",
    ],
  },
  {
    version: "0.26.0",
    date: "June 10, 2026",
    title: "In-App Update Notifications & Build Info",
    description:
      "Pollenate now tells you the moment a new version ships — and shows you exactly what changed.",
    features: [
      'Update badge in the sidebar — when a new version of Pollenate goes live, an "Update available" button appears; click it to see what\'s new and reload into the latest build',
      "What's New dialog — release notes open automatically the first time you run a new version, so you never miss a feature",
      'Live deploy awareness — while an update is rolling out, the badge shows a gentle "Updating…" indicator',
      "New About section in Settings — see your exact app version, build hash, and build date, check for updates on demand, and copy build info for support requests",
      "Desktop app updates surface in the same badge — one click downloads, installs, and relaunches",
    ],
  },
  {
    version: "0.25.0",
    date: "June 9, 2026",
    title: "Desktop App & Push Notifications",
    description:
      "Pollenate now lives on your desktop — and tells you the moment new feedback arrives, whether you're in the app, in your browser, or away from both.",
    features: [
      "New desktop app for macOS (Apple silicon & Intel) and Windows — grab it from the new Download page",
      "Native desktop notifications when new feedback comes in, even with the window closed to the tray",
      "Browser push notifications — enable them per browser from your Profile page and get notified without keeping a tab open",
      "Automatic updates — the desktop app checks for new signed builds on launch and installs them in one click",
      "Sign in on desktop with your existing account — email codes work right in the app, and Google/Apple/LinkedIn/BlueHive SSO hands off to your browser securely",
    ],
  },
  {
    version: "0.24.0",
    date: "June 9, 2026",
    title: "Rank-Order Questions",
    description:
      "Ask respondents to rank a list of options by dragging them into their preferred order — perfect for prioritization surveys, feature voting, and preference research.",
    features: [
      'New "Rank Order" question type for feedback pages — define a list of options and respondents drag and drop them into their preferred order',
      "Touch- and keyboard-accessible drag-and-drop ranking, powered by the same dnd-kit engine used across the dashboard",
      "Ranking analytics — the Responses tab shows each option's average rank position (lower is better), ordered from most to least preferred",
      "Individual rankings are displayed in order in the responses table and exported as a numbered list in CSV",
      "Available to the AI form generator, the public API, and Google Forms-style imports",
    ],
    improvements: [
      'Documentation, OpenAPI spec, and marketing pages updated to cover the new "ranking" type (now 16 question types)',
    ],
  },
  {
    version: "0.23.0",
    date: "May 13, 2026",
    title: "Import from Google Forms",
    description:
      "Migrate existing Google Forms into Pollenate feedback pages in a couple of clicks — no Apps Script, no OAuth, no AI calls.",
    features: [
      'Google Forms importer — new "Google Forms" tab in the + New Page dialog accepts a public form URL and creates a matching feedback page',
      "Supports both docs.google.com/forms/... and forms.gle/... short links; editor /edit URLs are normalized to /viewform automatically",
      "Imports title, description, and questions — SHORT_ANSWER, PARAGRAPH, MULTIPLE_CHOICE, CHECKBOXES, DROPDOWN, LINEAR_SCALE (1–5 → stars, 0–10 → NPS), DATE, SECTION_HEADER / PAGE_BREAK, and IMAGE/VIDEO captions",
      "Unsupported types (GRID, TIME, FILE_UPLOAD) are surfaced in a warning so you can re-add them manually instead of failing the import",
      "Parsed-form preview before saving, so you can confirm the questions before clicking Create Page",
    ],
    improvements: [
      "Documentation: new \"Import from Google Forms\" section under Feedback Pages with the full question-type mapping table, what doesn't carry over, and troubleshooting tips",
    ],
    fixes: [
      "Resolved pre-existing TypeScript errors in useChangelogToast (empty changelog guard), SettingsPage (dead invite-link role check), and the worker MCP handler (dual @modelcontextprotocol/sdk version bridge)",
    ],
  },
  {
    version: "0.22.0",
    date: "March 26, 2026",
    title: "Feedback Lifecycle, Kanban Board, Digest Emails & Admin Tools",
    description:
      "Track feedback from new → resolved with a Kanban board, close the loop with follow-up emails, get daily or weekly digest summaries, verify your domain, and manage organizations from the admin panel.",
    features: [
      "Feedback status lifecycle — every feedback item now moves through new → reviewing → planned → resolved → archived, with a full audit trail",
      "Kanban board — drag-and-drop feedback between status columns on the new /board page with live column counts",
      "Close-the-loop follow-up emails — send a follow-up message to the original respondent directly from the feedback detail page, auto-recorded as a note",
      "Daily and weekly digest emails — rich HTML summaries of feedback volume trends, NPS/CSAT scores, status queue breakdown, and notable quotes delivered on your schedule",
      "Digest preferences — choose daily, weekly, or no digest from your Profile page (defaults to weekly for all org members)",
      "Domain verification — prove ownership of your domain via DNS TXT record to enable domain-based org joining",
      "Access requests — users with a verified domain email can request to join your organization",
      "Dashboard changelog toast — a non-intrusive notification appears when you log in after a new release, with a direct link to the changelog",
      "App-wide toast notification system — success, error, warning, and info toasts powered by @mieweb/ui are now available across all pages",
    ],
    improvements: [
      "Settings page redesigned — vertical sidebar navigation with grouped sections (Organization, Team, Security, Developer) and icons on desktop, horizontal tabs on mobile",
      "Onboarding page expanded — domain-based org join flow lets new users find and request access to existing organizations",
      "Admin org detail page expanded — domain management, access request review, and archive controls",
      "Slug normalization — client-side input normalization (lowercase, alphanumeric + hyphens) with a 50-character max length",
      "Changelog data extracted to a shared module — enables build-time version checks and reuse across components",
    ],
  },
  {
    version: "0.21.0",
    date: "March 23, 2026",
    title: "Expanded Field Types, Customizable Submit Button & More",
    description:
      "Seven new question types for feedback pages — from layout controls to validated inputs — plus smarter AI generation, per-field validation, customizable submit button text, and single-line text fields.",
    features: [
      "Customizable submit button text — configure the label shown on the submit button for each feedback page via the Settings tab (e.g. \"Submit\", \"Send\", \"Do it!\")",
      "Content block question type — embed rich markdown content (headings, lists, links, images) between questions for instructions or context",
      "Section header question type — visually separate groups of questions with a title and horizontal rule",
      "Email input question type — dedicated email field with built-in format validation on client and server",
      "Phone input question type — phone number field with automatic US formatting and configurable format (US, International, Any)",
      "Number input question type — numeric field with optional min, max, step, and unit label",
      "Date input question type — date picker with optional time component (date-only or datetime)",
      "Dropdown question type — single-select dropdown from a configurable list of options",
      "Text field validation — existing text questions can now enforce email, phone, URL, or custom regex validation with configurable error messages",
      "Single-line / multi-line toggle for text questions — choose between a single-line input or multi-line textarea when creating or editing text questions",
      "AI feedback generator now understands multiline — AI-generated forms automatically use single-line inputs for short fields (name, email, phone) and multi-line textareas for longer responses",
      "Question badges in editor — text questions now show a \"Single-line\" or \"Multi-line\" badge in the question list for quick visibility",
    ],
    improvements: [
      "AI form generator understands all new field types — describe your form and AI will use email, phone, number, date, dropdown, section headers, and content blocks as appropriate",
      "Display-only types (content block, section header) are automatically excluded from required-field checks, submission payloads, analytics, and response tables",
      "Server-side validation for all new input types — email regex, phone digit count, number range, date parsing, and text validation config enforced on the worker",
      "Editor UI shows contextual badges for validation type, phone format, and number range on question cards",
      "Placeholder text support for email, phone, number, and text fields",
      "OpenAPI specification now documents all Feedback Pages endpoints (CRUD, AI generation, public submit) and all 15 question types with QuestionConfig schema",
      "Documentation page updated with complete question types table including all new types, corrected API values, and display-only indicators",
      "Open Graph and Twitter Card meta tags on all public pages — shared links now show rich previews with titles, descriptions, and images",
      "Preconnect hints for external origins to speed up page loads",
    ],
    fixes: [
      "Fixed feedback pages card layout — action buttons now stay anchored at the bottom of each card regardless of content height",
    ],
  },
  {
    version: "0.19.0",
    date: "March 12, 2026",
    title: "Feedback Page Editor Upgrades & @Mentions",
    description:
      "The feedback page question editor now supports drag-and-drop reordering, inline type changes, and optional follow-up text fields on rating questions. Notes on feedback also support @mentions.",
    features: [
      "Drag-and-drop question reordering — grab the handle to reorder questions with instant visual feedback and automatic server sync",
      "Change question type after creation — edit any question and swap its type (e.g. thumbs → NPS) without deleting and recreating",
      "Optional follow-up text field on rating questions — add a free-text textarea below thumbs, stars, CSAT, NPS, or emoji inputs for richer responses",
      "@Mentions in feedback notes — type @ to mention team members with autocomplete, highlighted in notes",
    ],
    improvements: [
      "Question cards now show follow-up prompt text when configured",
      "Rating inputs on public feedback pages preserve both score and text answer in a single submission",
      "Dashboard empty state for new users — welcoming onboarding screen with actionable CTAs (Set Up a Widget, Create a Feedback Page, View Documentation) instead of blank charts",
      "API errors now surface in a visible, dismissible banner on the dashboard instead of failing silently",
    ],
  },
  {
    version: "0.18.0",
    date: "March 9, 2026",
    title: "One-at-a-Time Feedback Pages & Passkey 2FA",
    description:
      "Feedback pages can now display questions one at a time with a progress bar, support rich markdown intros, and passkeys count toward your org's 2FA requirement.",
    features: [
      "One-at-a-time display mode for feedback pages — show questions individually with Next/Previous navigation and a progress bar instead of all at once",
      "Markdown introduction block for feedback pages — add rich-text intros with headings, lists, and links rendered above the questions",
      "Admin settings tab: display mode picker and markdown editor with live preview",
    ],
    improvements: [
      "Passkeys (Touch ID, Face ID, security keys) now satisfy organization-level 2FA enforcement — no separate TOTP setup required",
      "Added otpauth:// link on TOTP QR code for Safari/macOS Passwords autofill during 2FA setup",
      "Markdown preview in admin editor now uses marked + DOMPurify with proper prose styling instead of naive regex rendering",
      "Installed @tailwindcss/typography plugin for consistent heading, list, and link styles in markdown content",
    ],
    fixes: [
      "Fixed Turnstile CAPTCHA verification in one-at-a-time mode — widget now renders only on the final step and properly re-renders when navigating between steps",
      "Fixed invite revoke calling the wrong API endpoint (was using token URL instead of invitation ID URL)",
      "Fixed invite remind button — now shows 'Sent!' visual feedback after successfully resending an invitation",
      "Updated 2FA enforcement messaging to mention passkeys as an alternative to authenticator apps",
    ],
  },
  {
    version: "0.17.0",
    date: "March 6, 2026",
    title: "MCP Server for AI Agents",
    description:
      "Pollenate now exposes a Model Context Protocol (MCP) server at /mcp, allowing AI agents like Claude, Cursor, and other MCP-compatible clients to submit feedback, query stats, and search your data programmatically.",
    features: [
      "MCP server endpoint at https://api.pollenate.dev/mcp — uses Streamable HTTP transport per the latest MCP specification",
      "submit_feedback tool — AI agents can submit feedback to any inbox using an existing API key",
      "list_inboxes & list_brands tools — discover available inboxes and brands for the organization",
      "get_feedback tool — list and filter feedback by brand, inbox, type, date range with pagination",
      "get_feedback_stats tool — aggregated statistics including NPS, CSAT scores, breakdowns by type and source",
      "search_feedback tool — keyword search across comments and context fields",
      "Feedback submitted via MCP is tagged with source 'mcp' for tracking",
    ],
    improvements: [
      "API key authentication reused across MCP tools — existing pk_live_ keys work with collect, read, or write scopes",
      "Brand-scoped API keys automatically restrict MCP tool access to the associated brand",
    ],
    fixes: [
      "Fixed API documentation link in LandingPage footer — now correctly points to https://api.pollenate.dev/docs",
      "Fixed multi-choice question summary stats showing 0 responses — now correctly reads from the multi_values column using json_each() for accurate option counts",
      "Fixed email notification reliability — Mailgun response status is now checked and errors are properly logged instead of silently succeeding",
    ],
  },
  {
    version: "0.16.0",
    date: "March 6, 2026",
    title: "Invite Links, Brand Theming & Polished UX",
    description:
      "Discord-style invite links for onboarding teammates, full brand theming on feedback pages and widgets, a new Pollenate logo, and dozens of UX polish touches across every page.",
    features: [
      "Discord-style invite links — generate shareable join links with role, expiry, and max-use controls so teammates can join your org in one click",
      "Brand access on invite links — choose which brands new members get access to when they join",
      "Edit invite links after creation — update label, role, expiry, max uses, brands, and active status from a dedicated modal",
      "Feedback pages fully inherit brand theming — colors, fonts, logo, border radius, and button text color all flow from your brand settings",
      "Widget brand identity — pass a logo, brand name, and secondary color directly to the embeddable widget via data attributes",
      "Clickable brand buttons on the Widget Builder and Inboxes pages — select a brand inline instead of relying on the sidebar",
      'New Pollenate BrandMark logo SVG with light/dark variants and a "Powered by Pollenate" footer on public feedback pages',
      "Theme-aware favicon — automatically switches between light and dark favicon based on system preference",
      "Email notifications on feedback pages — add email recipients directly from the feedback page settings tab; creates automations behind the scenes with a CTA for advanced webhook/Zapier integrations",
    ],
    improvements: [
      'Rewritten documentation with a new "How Pollenate Works" overview section written in plain English for non-technical users',
      "Docs now include beginner-friendly explanations for every section — organizations, brands, inboxes, widgets, and feedback pages",
      'Inboxes page includes a description of what inboxes are and a "Learn more in the docs" link',
      "Shared WidgetPreview component — live iframe-based widget preview replaces hand-coded mocks across Brand Editor and Widget Builder",
      "Replaced all 7 native browser confirm() dialogs with a polished modal-based useConfirmDialog hook",
      "UTM tracking links on all BlueHive Health footer references for analytics",
      "Invite and Join pages redesigned with cleaner layout and better error handling",
    ],
    fixes: [
      "Fixed brand logo upload — media URLs now resolve correctly through a dedicated /brands/:id/logo proxy endpoint",
      "XSS protections added to widget.js — escapeHtml and isSafeUrl prevent injection when rendering user-supplied strings",
      "Invite link security hardened — brand IDs validated against org, atomic max_uses enforcement, idempotent join, race-safe code generation",
      "Accessibility check color contrast fallbacks fixed for dark mode in Brand Editor",
      "Stabilized useEffect dependencies in WidgetsPage to prevent resetting widget edits on context recreation",
      "Switched from <a href> to React Router <Link> for client-side SPA navigation on Inboxes page",
    ],
  },
  {
    version: "0.15.0",
    date: "March 4, 2026",
    title: "Reporting Dashboard & AI Insights",
    description:
      "Interactive charts, filterable analytics, and AI-powered feedback summaries — plus major authentication stability improvements.",
    features: [
      "Interactive analytics charts — feedback volume area chart, NPS/CSAT trend lines, type distribution donut, and sparklines on dashboard stat cards",
      "Inbox detail charts — impressions vs. feedback area chart and conversion rate bar chart",
      "Filter bar with Inbox, Type, and Source dropdowns — filters flow through to all stats, charts, and feedback lists",
      "Date range picker with 7d / 30d / 90d / 6-month presets for all analytics views",
      "Source and inbox breakdown charts — pie chart for feedback sources, bar chart for inbox distribution",
      "AI-powered insights panel — generates a natural-language summary of feedback trends, sentiment, and actionable recommendations using GPT-4.1-mini",
      "Backend: daily NPS and CSAT time series, type/source filters, and bySource/byInbox breakdowns on the stats endpoint",
    ],
    improvements: [
      "Feedback page editor charts replaced with interactive recharts components instead of hand-drawn SVGs",
      "Accessibility check component extracted into a reusable shared module",
    ],
    fixes: [
      "Prevented stale tokens from killing concurrent login sessions — added skipAuth for public auth endpoints and scoped logout to only affect current tokens",
      "Fixed logout endpoint checking wrong JWT field (payload.id → payload.sub), so sessions are now correctly deleted on logout",
      "Only logout on SessionExpiredError, not transient network errors or 500s — users stay authenticated through temporary API hiccups",
      "Navigate directly after OTP/MFA/passkey login instead of relying on useEffect — eliminates race condition where users bounced back to the login page",
      "Wait for Zustand persist hydration before running refreshUser to avoid racing with uninitialized store state",
      "Replaced hono/trailing-slash 301 redirect with custom 302 to prevent browsers from permanently caching redirects that strip Authorization headers",
    ],
  },
  {
    version: "0.14.0",
    date: "February 26, 2026",
    title: "Gravatar Avatars, Invitation Reminders & Docs Overhaul",
    description:
      "User avatars via Gravatar, one-click invitation reminders, a complete documentation rewrite with CSP troubleshooting, and dependency updates.",
    features: [
      "Gravatar user avatars — profile pictures resolve automatically from email via SHA-256 hash, with initials fallback",
      "Resend / remind button for pending invitations — regenerates the invite token, resets expiry to 7 days, and sends a reminder email",
      "Expired and expiring-soon badges on the invitations table so admins can see at a glance which invites need attention",
      "QR code generation API endpoint for programmatic QR code creation",
    ],
    improvements: [
      'Complete documentation rewrite — 1,700+ lines added with user-friendly language, step-by-step guides, and "In plain English" callouts for non-technical readers',
      "New Content Security Policy troubleshooting section — common errors, required directives table, and config examples for Nginx, Apache, Next.js, and meta tags",
      'New CSP FAQ entry for "Failed to fetch" errors with direct link to the troubleshooting section',
      "Docs sidebar gains a Troubleshooting group with CSP as the first entry",
    ],
    fixes: [
      "Fixed BrandAccessModal checkbox handler — switched from onCheckedChange to onChange for compatibility",
      "Bumped all dependencies to latest (React 19.2.4, react-router 7.13.1, Hono 4.12.2, Tailwind 4.2.1, AG Grid 35.1.0, Wrangler 4.68.1)",
      "Fixed AG Grid import path — DashboardPage now imports from @mieweb/ui/ag-grid",
    ],
  },
  {
    version: "0.13.0",
    date: "February 16, 2026",
    title: "AI Brand Extraction & Streamlined Settings",
    description:
      "Automatically extract brand colors and styles from any website URL, plus a cleaner navigation with API Keys and Automations moved into Settings.",
    features: [
      "AI-powered brand style extraction — paste a website URL and Pollenate auto-detects colors, fonts, and brand name",
      "API Keys and Automations are now tabs inside the Settings page for a cleaner sidebar",
    ],
    improvements: [
      "Simplified sidebar navigation — fewer top-level items, more organized settings",
      "Legacy /api-keys and /automations URLs still work seamlessly",
    ],
  },
  {
    version: "0.12.0",
    date: "February 13, 2026",
    title: "React Package & AI Form Generation",
    description:
      "Official React component package, AI-powered form builder, and short URLs for feedback pages.",
    features: [
      "Published @pollenate/react — a fully typed, drop-in React component for embedding Pollenate widgets",
      "AI-powered feedback form generation — describe your survey and let AI build the form for you",
      "Short URLs for feedback pages with shareable links and QR codes",
    ],
    fixes: [
      "Fixed refresh token loss and concurrent refresh race conditions",
      "Resolved CORP header issues for QR code sharing",
    ],
  },
  {
    version: "0.11.0",
    date: "February 9, 2026",
    title: "Zapier Integration & Automation Engine",
    description:
      "Connect Pollenate to 7,000+ apps with our official Zapier integration and new automations engine.",
    features: [
      "Zapier integration — trigger workflows from new feedback, search existing feedback, and more",
      "Automation engine with support for webhooks, email alerts, and Zapier triggers",
      "Brand access management — control which team members can access specific brands",
      "Feedback source tracking — see whether feedback came from the widget, API, Zapier, or feedback pages",
      "Dynamic score fields in Zapier based on feedback type (stars, NPS, CSAT, etc.)",
      "Deep-link to API key creation from Zapier setup flow",
    ],
    fixes: [
      "Fixed webhook payload missing fields and notes foreign key constraint",
      "Resolved Zapier publishing compatibility checks",
      "Fixed react-router import for useSearchParams",
    ],
  },
  {
    version: "0.10.0",
    date: "February 7, 2026",
    title: "Feedback Pages & Brand Editor",
    description:
      "Build multi-question feedback forms, customize your brand, and share via QR codes.",
    features: [
      "Feedback Pages — drag-and-drop multi-question survey builder with public shareable forms",
      "QR code generation for feedback pages — print, share in emails, or display in-store",
      "Brand editor with full widget setup and live preview",
      "Button text color customization for branded widgets",
      "Sidebar collapse state persisted in localStorage",
    ],
    improvements: [
      "Improved hero CTA and widget preview layout on mobile",
      "Better mobile responsiveness across all landing pages",
    ],
  },
  {
    version: "0.9.0",
    date: "February 6, 2026",
    title: "Two-Factor Authentication & Security",
    description:
      "Organization-level 2FA enforcement and security improvements.",
    features: [
      "Organization-level 2FA enforcement — require all team members to use two-factor authentication",
      'Group inboxes by brand when "All Brands" is selected',
      "Privacy controls — data-strip-query-params option to strip sensitive URL parameters",
      "Inline container mode for widget embedding — mount inside any DOM element",
    ],
    fixes: [
      "Prevented double OTP verification and sanitized post-login redirects",
      "Fixed landing page flash on non-prerendered routes",
      "Fixed pre-rendered content visibility on non-root routes",
    ],
  },
  {
    version: "0.8.0",
    date: "February 5, 2026",
    title: "AI Agents, Billing & Admin Tools",
    description:
      "AI agent skill files, Stripe billing integration, and structured feedback context.",
    features: [
      "AI Agent Integration — skill files and documentation for AI agents to submit feedback",
      "Stripe billing with usage metering and plan limit enforcement",
      "Feedback detail page with notes and external tracking links",
      "Context column in feedback tables — attach structured metadata to feedback",
      "Structured context data UI with raw JSON toggle",
      "Robots.txt and sitemap.xml for SEO",
      "Dashboard link with user avatar in landing page navigation",
      "Pollenate dogfooding widget on production site",
    ],
    improvements: [
      "Improved timezone handling with Luxon for correct UTC→local conversion",
    ],
    fixes: ["Removed loading spinner flash on landing page navigation"],
  },
  {
    version: "0.7.0",
    date: "February 4, 2026",
    title: "Widget Analytics & Dashboard",
    description:
      "Impression tracking, AG Grid feedback tables, and the embeddable widget.",
    features: [
      "Widget impression tracking and pageview analytics",
      "AG Grid powered feedback table with sorting, filtering, and export",
      "Dashboard stats aggregation with trend analysis",
      "Embeddable feedback widget — single script tag, works anywhere",
      "Documentation page with comprehensive API reference",
    ],
    fixes: [
      "Fixed UTF-8 charset header for widget.js to fix emoji rendering",
      "Allowed any origin for widget public endpoints (CORS)",
      "Improved script tag detection for async loading",
    ],
  },
  {
    version: "0.1.0",
    date: "February 4, 2026",
    title: "Initial Release",
    description:
      "The very first release of Pollenate Cloud — feedback collection platform built on Cloudflare Pages and Workers.",
    features: [
      "Core feedback collection API with support for thumbs, stars, NPS, CSAT, emoji, and text",
      "Real-time dashboard with analytics",
      "Inbox management with API key authentication",
      "User authentication with OTP email login",
      "Cloudflare D1 database with Workers API",
    ],
  },
];

/** The latest changelog version string (e.g. "0.22.0") */
export const LATEST_CHANGELOG_VERSION = changelog[0]!.version;

/**
 * localStorage key tracking the last release the user has seen. Written by
 * the What's New modal (on close) and the /changelog page (on visit).
 */
export const CHANGELOG_STORAGE_KEY = "lastSeenChangelogVersion";
